import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import { Composer } from '../components/Composer'
import { EnrollmentList, PlaybookModal } from '../components/Playbooks'
import { Icon } from '../components/Icon'
import { ErrorState, Loading } from '../components/States'
import { TagEditor } from '../components/TagEditor'
import { TaskList } from '../components/TaskList'
import { Timeline } from '../components/Timeline'
import { useToast } from '../components/Toast'
import { initials, roleList, titleCase, usd } from '../lib/format'
import { tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

/** A third party on your loan files: an agent, a title officer, an appraiser. They
 *  are not contacts — the CRM knows them because they turned up on files — so this
 *  page reads as a partner record, and a borrower who IS a contact redirects to
 *  their contact card instead. */
export function PersonView() {
  const { personKey = '' } = useParams()
  const { data, error, loading, reload } = useAsync(() => api.person(personKey), [personKey])
  const [tags, setTags] = useState<string[] | null>(null)
  const [bump, setBump] = useState(0)
  const [busy, setBusy] = useState(false)
  const [workflow, setWorkflow] = useState(false)
  const nav = useNavigate()
  const toast = useToast()

  useEffect(() => {
    if (data?.contact_id) nav(`/contacts/c/${data.contact_id}`, { replace: true })
  }, [data, nav])

  if (loading || data?.contact_id) return <div className="page"><Loading /></div>
  if (error || !data) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  const roles = roleList(data.roles)
  const name = data.name ?? data.company ?? '—'
  const subject = { person_key: data.person_key }

  async function makeContact() {
    setBusy(true)
    try {
      const { contact_id } = await api.personToContact(data!.person_key)
      nav(`/contacts/c/${contact_id}`)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setBusy(false)
    }
  }

  return (
    <div className="page">
      <div className="crumbs">
        <Link to="/partners">Partners</Link> › {name}
      </div>

      <div className="card">
        <div className="detail-head">
          <span className="detail-av" style={tint(name)}>{initials(name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 className="h1">{name}</h1>
              {roles.map((r) => <span className="badge t-neutral" key={r}>{titleCase(r)}</span>)}

            </div>
            <p className="subline" style={{ fontSize: 13 }}>
              {[data.company !== name ? data.company : null, data.email, data.phone].filter(Boolean).join(' · ') || '—'}
            </p>
            <p className="subline" style={{ fontSize: 12.5, marginTop: 2 }}>
              A third party on {data.loans} of your loan file{data.loans === 1 ? '' : 's'} — not a contact. Add them as one
              to text or email them and record consent.
            </p>
            <div style={{ marginTop: 9 }}>
              <TagEditor subject={subject} tags={tags ?? data.tags} onChange={setTags} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {data.phone ? (
              <a className="iconbtn lg" href={`tel:${data.phone.replace(/[^\d+]/g, '')}`} title="Call"><Icon name="call" size={18} /></a>
            ) : null}
            <button className="btn" onClick={() => setWorkflow(true)}>
              <Icon name="bolt" size={17} /> Start workflow
            </button>
            <button className="btn primary" onClick={makeContact} disabled={busy}
              title="Creates a contact record you own, so you can text and email them and record consent">
              <Icon name="person_add" size={17} /> {busy ? 'Adding…' : 'Add as a contact'}
            </button>
          </div>
        </div>
      </div>

      <div className="body-3col" style={{ marginTop: 16 }}>
        <section className="card">
          <div className="card-head"><h2 className="h2">Contact info</h2></div>
          <div className="card-body" style={{ paddingTop: 4 }}>
            <div className="kvrow"><span className="k">Email</span>
              <span className="v">{data.email ? <a href={`mailto:${data.email}`}>{data.email}</a> : '—'}</span></div>
            <div className="kvrow"><span className="k">Phone</span><span className="v">{data.phone ?? '—'}</span></div>
            <div className="kvrow"><span className="k">Company</span>
              <span className="v">{data.company ?? '—'}</span></div>
            <div className="kvrow"><span className="k">Files worked</span><span className="v">{data.loans}</span></div>
            <div className="kvrow"><span className="k">Volume on them</span><span className="v">{usd(data.volume)}</span></div>
            <div className="kvrow"><span className="k">Of those, funded</span><span className="v">{data.funded}</span></div>
          </div>
        </section>

        <section className="card">
          <div className="card-head"><h2 className="h2">Activity</h2></div>
          <div className="card-body">
            <Composer subject={subject} onLogged={() => setBump((n) => n + 1)} />
            <Timeline subject={subject} refreshKey={bump} />
          </div>
        </section>

        <div className="stack">
          <section className="card">
            <div className="card-head"><h2 className="h2">Tasks</h2></div>
            <div className="card-body" style={{ paddingTop: 4 }}><TaskList subject={subject} refreshKey={bump} /></div>
          </section>
          <section className="card">
            <div className="card-head">
              <h2 className="h2">Workflows</h2>
              <span className="spacer" />
              <button className="btn sm" onClick={() => setWorkflow(true)} title="Start one of your workflows for this person">
                <Icon name="add" size={15} /> Add
              </button>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              <EnrollmentList subject={subject} refreshKey={bump} onChange={() => setBump((n) => n + 1)}
                onAdd={() => setWorkflow(true)} />
            </div>
          </section>
          <section className="card">
            <div className="card-head"><h2 className="h2">Files they worked on</h2></div>
            <div className="quiet" style={{ padding: '0 16px', fontSize: 12 }}>Other people’s loans — they’re the {roles[0] ? titleCase(roles[0]).toLowerCase() : 'third party'} on each.</div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {data.loans_detail.length ? data.loans_detail.map((l) => (
                <Link key={`${l.loan_id}-${l.role}`} to={`/loans/${encodeURIComponent(l.loan_id)}`} className="condrow" style={{ color: 'inherit' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{l.property_street ?? l.loan_number}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {l.borrower ? <>{l.borrower}’s loan · </> : null}{titleCase(l.role)} · {titleCase(l.status)}
                    </span>
                  </span>
                  <span className="mono" style={{ fontSize: 12.5 }}>{usd(l.loan_amount)}</span>
                </Link>
              )) : <p className="quiet">No loan files.</p>}
            </div>
          </section>
        </div>
      </div>
      <PlaybookModal open={workflow} onClose={() => setWorkflow(false)} subject={subject}
        onEnrolled={() => setBump((n) => n + 1)} firstName={data.name?.split(' ')[0]} />
    </div>
  )
}
